import { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { FileText, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface DocumentProcessingProps {
  onComplete?: () => void;
}

type ProcessingStepId = "upload" | "extract" | "analyze" | "finalize";

interface ProcessingStep {
  id: ProcessingStepId;
  status: "pending" | "processing" | "complete";
}

const TOTAL_PROCESSING_MS = 60000;
const PROGRESS_TICK_MS = 600; // 100 * 600ms = 60s
const PROGRESS_INCREMENT = 1;
const MESSAGE_ROTATION_MS = 10000; // 10s per message

const STEP_TIMINGS = [
  { step: 1, time: 15000 }, // 15s
  { step: 2, time: 30000 }, // 30s
  { step: 3, time: 45000 }  // 45s
];

const PROCESSING_MESSAGES = [
  {
    title: "Guests Can Ask for Anything, Anytime",
    body:
      "Conciergie becomes a 24/7 concierge that answers questions, handles requests, " +
      "and guides guests instantly, even when staff is busy or unavailable.",
  },
  {
    title: "Knows Everything about Your Hotel Instantly",
    body:
      "Upload your guest directory, menus, and local tips. Conciergie retains it all and " +
      "provides hotel-specific answers, not generic AI guesses.",
  },
  {
    title: "Reduces Front-Desk Workload by 60–70 Percent",
    body:
      "Conciergie handles the repetitive questions: check-in times, Wi-Fi, breakfast, " +
      "amenities, parking, directions, spa hours, etc.\n\n" +
      "Your staff focuses on meaningful interactions, not interruptions.",
  },
  {
    title: "Turns Every Conversation Into Revenue",
    body:
      "Conciergie naturally suggests upsells: late checkout, spa, dining, room upgrades, " +
      "partner experiences.\n\n" +
      "No pressure, no scripts, just intelligent, context-aware recommendations.",
  },
  {
    title: "Works for Every Guest, in Every Language",
    body:
      "Guests interact in their own language, hotels receive requests in theirs.\n\n" +
      "No translation mistakes, no misunderstandings — perfect for international travelers.",
  },
];

export function DocumentProcessing({ onComplete }: DocumentProcessingProps) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [steps, setSteps] = useState<ProcessingStep[]>([
    { id: "upload", status: "complete" },
    { id: "extract", status: "processing" },
    { id: "analyze", status: "pending" },
    { id: "finalize", status: "pending" }
  ]);
  const [activeMessageIndex, setActiveMessageIndex] = useState(0);

  useEffect(() => {
    // Simulate progress over ~60 seconds
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + PROGRESS_INCREMENT;
      });
    }, PROGRESS_TICK_MS);

    // Simulate step progression over the same 60 seconds
    const stepTimeouts = STEP_TIMINGS.map(({ step, time }) =>
      setTimeout(() => {
        setSteps((prev) =>
          prev.map((s, index) => {
            if (index < step) return { ...s, status: "complete" };
            if (index === step) return { ...s, status: "processing" };
            return s;
          })
        );
      }, time)
    );

    // Rotate marketing messages while user waits
    const messageInterval = setInterval(() => {
      setActiveMessageIndex((prev) => (prev + 1) % PROCESSING_MESSAGES.length);
    }, MESSAGE_ROTATION_MS);

    // Complete after all steps (~60s)
    const completeTimeout = setTimeout(() => {
      setSteps((prev) => prev.map((s) => ({ ...s, status: "complete" })));
      if (onComplete) {
        setTimeout(() => onComplete(), 1000);
      }
    }, TOTAL_PROCESSING_MS);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      stepTimeouts.forEach((t) => clearTimeout(t));
      clearTimeout(completeTimeout);
    };
  }, [onComplete]);

  const currentMessage = PROCESSING_MESSAGES[activeMessageIndex];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Card className="border-2">
          <CardContent className="pt-12 pb-12">
            {/* Icon and Title */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                <FileText className="h-10 w-10 text-blue-600" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-24 w-24 text-blue-600 animate-spin opacity-20" />
                </div>
              </div>
              
              <h1 className="text-3xl mb-3">{t("processing.title")}</h1>
              <p className="text-gray-600 mb-2">
                {t("processing.description")}
              </p>
              <p className="text-sm text-gray-500">
                {t("processing.duration")}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
              <Progress value={progress} className="h-3" />
              <p className="text-sm text-gray-500 text-center mt-2">
                {t("processing.progress", { value: progress })}
              </p>
            </div>

            {/* Processing Steps */}
            <div className="space-y-4 mb-8">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className="flex items-center gap-3 p-3 rounded-lg transition-colors"
                  style={{
                    backgroundColor:
                      step.status === "complete"
                        ? "#f0fdf4"
                        : step.status === "processing"
                        ? "#eff6ff"
                        : "#f9fafb"
                  }}
                >
                  <div className="flex-shrink-0">
                    {step.status === "complete" && (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    )}
                    {step.status === "processing" && (
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    )}
                    {step.status === "pending" && (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    )}
                  </div>
                  <span
                    className={`text-sm ${
                      step.status === "complete"
                        ? "text-green-900"
                        : step.status === "processing"
                        ? "text-blue-900"
                        : "text-gray-500"
                    }`}
                  >
                    {t(`processing.steps.${step.id}`)}
                  </span>
                </div>
              ))}
            </div>

            {/* Marketing Carousel */}
            <div className="mb-8">
              <div className="bg-white border border-blue-100 rounded-lg p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-blue-500 mb-1">
                  {`Why hotels use Conciergie (${activeMessageIndex + 1}/${PROCESSING_MESSAGES.length})`}
                </p>
                <h2 className="text-base font-semibold text-gray-900 mb-2">
                  {currentMessage.title}
                </h2>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {currentMessage.body}
                </p>
                <div className="flex gap-1 mt-3 justify-center">
                  {PROCESSING_MESSAGES.map((_, idx) => (
                    <span
                      key={idx}
                      className={`h-1.5 w-4 rounded-full transition-colors ${
                        idx === activeMessageIndex ? "bg-blue-600" : "bg-blue-200"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Info Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-sm text-blue-900">
                {t("processing.note")}
              </p>
            </div>

            {/* Disabled Back Button */}
            <div className="mt-6 text-center">
              <Button variant="ghost" disabled className="cursor-not-allowed">
                {t("processing.back")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
